import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import Swal from "sweetalert2";
import axios from "axios";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
// Assets
import HeaderImage from "../assets/img/header-img.png";

const BookDemoForm = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    city: "",
    phone: "",
    company: "",
    date: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);

  const getCurrentDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!executeRecaptcha) {
      console.log("reCAPTCHA not loaded yet");
      return;
    }

    setLoading(true);

    try {
      const recaptchaToken = await executeRecaptcha('book_demo');
      
      if (!recaptchaToken) {
        setLoading(false);
        Swal.fire({
          title: "Validation Error",
          text: "Failed to generate reCAPTCHA token. Please try again.",
          icon: "warning",
          confirmButtonText: "Close",
          confirmButtonColor: "#7620ff",
          background: "#ffffff",
          borderRadius: "16px",
        });
        return;
      }

      await axios.post("/api/send-demo-email", { ...formData, recaptchaToken });
      
      Swal.fire({
        title: "Demo Booked!",
        text: "Thank you for your interest. We will contact you shortly to confirm your demo.",
        icon: "success",
        confirmButtonText: "Close",
        confirmButtonColor: "#7620ff",
        background: "#ffffff",
        borderRadius: "16px",
      });
      
      setFormData({
        name: "",
        email: "",
        city: "",
        phone: "",
        company: "",
        date: "",
        description: "",
      });
    } catch (error) {
      console.error("Error booking demo:", error);
      Swal.fire({
        title: "Oops!",
        text: "Something went wrong while booking your demo. Please try again later.",
        icon: "error",
        confirmButtonText: "Close",
        confirmButtonColor: "#d33",
        background: "#ffffff",
        borderRadius: "16px",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <LayoutContainer>
        <LeftColumn>
          <TextContent>
            <Title>Transform Your Workflow with AI</Title>
            <Description>
              Discover how our cutting-edge solutions can automate your processes, boost productivity, and drive your business forward. Schedule a personalized walkthrough today.
            </Description>
          </TextContent>
        </LeftColumn>

        <RightColumn>
          <Content>
            <FormHeader>
              <FormTitle>Book Your Demo</FormTitle>
              <FormSubtitle>Fill out the details below and we'll be in touch.</FormSubtitle>
            </FormHeader>
            <DemoFormWrapper>
              <Form onSubmit={handleSubmit}>
                <FormRow>
                  <FormGroup>
                    <Label>Full Name <Asterisk>*</Asterisk></Label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Email Address <Asterisk>*</Asterisk></Label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                    />
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <Label>Phone Number</Label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (234) 567-8900"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Company Name</Label>
                    <Input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Acme Corp"
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>City <Asterisk>*</Asterisk></Label>
                    <Input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      placeholder="New York"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Preferred Date & Time</Label>
                    <Input
                      type="datetime-local"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      min={getCurrentDateTime()}
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <Label>Description <Asterisk>*</Asterisk></Label>
                  <TextArea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    placeholder="Please provide details about what you would like to discuss..."
                    rows="4"
                  />
                </FormGroup>

                <SubmitButton type="submit" disabled={loading}>
                  {loading ? "Booking..." : "Confirm Booking"}
                </SubmitButton>
                <RecaptchaDisclaimer>
                  This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> apply.
                </RecaptchaDisclaimer>
              </Form>
            </DemoFormWrapper>
          </Content>
        </RightColumn>
      </LayoutContainer>
    </Wrapper>
  );
};

const BookDemo = () => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "YOUR_SITE_KEY_HERE"}>
      <BookDemoForm />
    </GoogleReCaptchaProvider>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.section`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 140px 20px 60px;
  background: #f9fafb;
`;

const LayoutContainer = styled.div`
  max-width: 1200px;
  width: 100%;
  display: flex;
  background: white;
  border-radius: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease-out;

  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

const LeftColumn = styled.div`
  flex: 1;
  background: linear-gradient(rgba(17, 24, 39, 0.7), rgba(118, 32, 255));
  // background: linear-gradient(rgba(17, 24, 39, 0.7), rgba(118, 32, 255, 0.8)), url(${HeaderImage});
  // background-size: cover;
  // background-position: center;
  padding: 50px 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media (max-width: 960px) {
    padding: 60px 20px;
    text-align: center;
    min-height: 300px;
  }
`;

const RightColumn = styled.div`
  flex: 1.2;
  padding: 50px 40px;
  background: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: 960px) {
    padding: 40px 20px;
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
`;

const TextContent = styled.div`
  text-align: center;
`;

const FormHeader = styled.div`
  margin-bottom: 30px;
`;

const FormTitle = styled.h2`
  font-size: 32px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 8px;
`;

const FormSubtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 16px;
  letter-spacing: -0.5px;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
`;

const Description = styled.p`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  margin: 0 auto;
  text-shadow: 0 1px 2px rgba(0,0,0,0.2);
`;

const Asterisk = styled.span`
  color: #ef4444;
  margin-left: 2px;
`;

const DemoFormWrapper = styled.div`
  width: 100%;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormRow = styled.div`
  display: flex;
  gap: 20px;
  
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 24px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  letter-spacing: 0.3px;
`;

const Input = styled.input`
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  font-size: 15px;
  background-color: #f9fafb;
  color: #1f2937;
  outline: none;
  transition: all 0.2s ease-in-out;
  
  &::placeholder {
    color: #9ca3af;
  }
  
  &:hover {
    background-color: #ffffff;
    border-color: #d1d5db;
  }

  &:focus {
    background-color: #ffffff;
    border-color: #7620ff;
    box-shadow: 0 0 0 4px rgba(118, 32, 255, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  font-size: 15px;
  background-color: #f9fafb;
  color: #1f2937;
  outline: none;
  transition: all 0.2s ease-in-out;
  resize: vertical;
  
  &::placeholder {
    color: #9ca3af;
  }
  
  &:hover {
    background-color: #ffffff;
    border-color: #d1d5db;
  }

  &:focus {
    background-color: #ffffff;
    border-color: #7620ff;
    box-shadow: 0 0 0 4px rgba(118, 32, 255, 0.1);
  }
`;

const SubmitButton = styled.button`
  margin-top: 10px;
  padding: 16px 24px;
  background: linear-gradient(135deg, #7620ff 0%, #5b10d6 100%);
  color: white;
  font-size: 17px;
  font-weight: 700;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(118, 32, 255, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(118, 32, 255, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const RecaptchaDisclaimer = styled.div`
  font-size: 12px;
  color: #6b7280;
  text-align: center;
  margin-top: 4px;
  
  a {
    color: #7620ff;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: #5b10d6;
      text-decoration: underline;
    }
  }
`;

export default BookDemo;

